import React, { useEffect, useState, useMemo } from 'react'

import { useToasts } from 'react-toast-notifications'
import Select from 'react-select'
import styled from 'styled-components'
import { IoCloseOutline } from 'react-icons/io5'

import Slider from './Assessments/components/Slider'
import { Button } from '../../Button'

import axiosInstance from '../../../services/config/AxiosIntance'

import useMutation from '../../../hooks/useMutation'

import { P } from '../../../style/Typography.style'

const Skills = (data) => {
  const talent = data.talent

  const { addToast } = useToasts()

  const [skillOptions, setSkillOptions] = useState([])
  const [selectedSkills, setSelectedSkills] = useState([])

  const {
    error,
    handleRequest,
    loading,
    success,
    data: skillData,
  } = useMutation()

  useEffect(() => {
    if (success) {
      addToast(skillData?.message, {
        appearance: 'success',
        autoDismiss: true,
      })
    }

    if (error) {
      addToast(
        error?.response?.data?.message ||
        'Something went wrong while saving skills',
        {
          appearance: 'error',
          autoDismiss: true,
        }
      )
    }
  }, [success, error])

  const minSkillLevel = useMemo(() => 1, [])
  const maxSkillLevel = useMemo(() => 5, [])
  const skillLevels = useMemo(
    () => ({
      1: 'Beginner',
      2: 'Novice',
      3: 'Skilled',
      4: 'Experienced',
      5: 'Expert',
    }),
    []
  )

  const getTechnicalSkills = async () => {
    const response = await axiosInstance.get(`/technical-skills?all=true`)

    const mappedSkills = response?.data?.resp?.map((skill) => ({
      value: skill?.skills,
      label: skill?.skills,
      experience: 1,
    }))
    setSkillOptions(mappedSkills)
  }

  const handleSkillChangeOptions = (selectedOptions) => {
    const updatedSkills = selectedOptions.map((option) => ({
      ...option,
      id: option.value,
      applicant_id: talent.id,
    }))
    setSelectedSkills(updatedSkills ?? [])

    localStorage.setItem(
      'cachedData',
      JSON.stringify({
        skill: updatedSkills,
      })
    )
  }

  const handleSkillLevelChange = (e, index) => {
    const updatedSkills = [...selectedSkills]
    updatedSkills[index].experience = e.target.value
    setSelectedSkills(updatedSkills)

    localStorage.setItem(
      'cachedData',
      JSON.stringify({
        skill: updatedSkills.map((skill) => ({
          ...skill,
          applicant_id: talent.id,
        })),
      })
    )
  }

  const handleSkillDelete = (index) => {
    const updatedSkills = [...selectedSkills]
    updatedSkills.splice(index, 1)
    setSelectedSkills(updatedSkills)

    localStorage.setItem(
      'cachedData',
      JSON.stringify({
        skill: updatedSkills.map((skill) => ({
          ...skill,
          applicant_id: talent.id,
        })),
      })
    )
  }

  useEffect(() => {
    getTechnicalSkills()
  }, [])

  useEffect(() => {
    const cachedData = localStorage.getItem('cachedData')
    const parsedCachedData = JSON.parse(cachedData)

    if (parsedCachedData) {
      const cachedSkills = parsedCachedData?.skill?.filter(
        (s) => s.applicant_id === talent.id
      )
      if (cachedSkills.length > 0) {
        setSelectedSkills(cachedSkills)
      }
    }
  }, [talent?.id])

  useEffect(() => {
    if (talent?.recruiter_input_skills) {
      const parsedRecruiterInputSkills = JSON.parse(
        talent?.recruiter_input_skills
      )
      setSelectedSkills(parsedRecruiterInputSkills)
    }
  }, [talent?.recruiter_input_skills])

  const handleSaveSkills = () => {
    const payload = {
      applicant_id: talent.id,
      skills_meta: JSON.stringify(selectedSkills),
      user_id: parseInt(localStorage.getItem('id')),
    }

    handleRequest('/recruiter-skill-input', payload)

    const cachedData = localStorage.getItem('cachedData')
    const parsedCachedData = JSON.parse(cachedData)
    localStorage.setItem(
      'cachedData',
      JSON.stringify({
        ...parsedCachedData,
        skill: [],
      })
    )
  }

  return (
    <StyledSkills>
      <P marginBottom="0.5rem">Technical Skills</P>

      <Select
        isMulti
        options={skillOptions}
        value={selectedSkills}
        onChange={handleSkillChangeOptions}
        placeholder="Select skills..."
        isSearchable
      />

      {loading && <P marginTop="0.5rem">Loading Applicants Skills...</P>}

      <StyledSkillsContainer>
        {selectedSkills.map((skill, index) => (
          <StyledSkill key={skill.value}>
            <StyledSkillTitle>
              <IoCloseOutline
                className="icon"
                onClick={() => handleSkillDelete(index)}
              />
              <P>{skill.label}</P>
            </StyledSkillTitle>
            <Slider
              key={skill.value}
              id={index}
              value={skill.experience}
              onChange={(e) => handleSkillLevelChange(e, index)}
              min={minSkillLevel}
              max={maxSkillLevel}
              labels={skillLevels}
            />
          </StyledSkill>
        ))}
      </StyledSkillsContainer>

      <Button
        type="button"
        width="8rem"
        height="2.5rem"
        fontSize="0.875rem"
        handleClick={handleSaveSkills}
      >
        Save Skills
      </Button>
    </StyledSkills>
  )
}

export default Skills

const StyledSkills = styled.div`
  min-height: 40vh;
  width: 100%;
  padding-bottom: 2rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
  }

  &::before {
    content: '';
    position: absolute;
  }
`

const StyledSkill = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledSkillsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.5rem;
`

const StyledSkillTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .icon {
    cursor: pointer;
    font-size: 1.25rem;
    color: #000;
  }
`
